// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useMemo } from 'react';
import { func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import useAsyncError from 'Hooks/useAsyncError';
import useCategories from 'Hooks/useCategories';
import useAnalytics from 'Hooks/useAnalytics';
import {
  API_ACCOUNT_SETTINGS,
  EMAIL_REGEX,
  CURRENCY_EUR,
  CURRENCY_USD,
  CURRENCY_RON,
} from 'Constants';
import AccountSettingsComponent from 'Components/Account/AccountSettings';

const defaultValues = {
  defaultCurrency: null,
  language: null,
  withVat: null,
  sessionLifetime: null,
  userPaymentOption: null,
  userTransportOption: null,
  technicalSupportEmail: '',
  alertEmail: '',
  alertOption: null,
  alertEmailLanguage: null,
  resellerPaymentOption: null,
  resellerTransportOption: null,
};

const AccountSettings = memo(({ setErrors }) => {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  const fetcher = useFetch();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const { data: { email, loggedIn, userId } = {}, mutate } = useUser();
  const { mutate: mutateCategories } = useCategories();
  const { isAdmin, isExternalReseller } = usePermissions();
  const { query, location } = useRouter();
  const analytics = useAnalytics();
  const {
    formState: { errors: rhfErrors },
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const CURRENCY_OPTIONS = useMemo(() => [
    { value: CURRENCY_EUR, label: t('common.currencies.eur') },
    { value: CURRENCY_USD, label: t('common.currencies.usd') },
    { value: CURRENCY_RON, label: t('common.currencies.ron') },
  ], [t]);

  const LANGUAGE_OPTIONS = useMemo(() => [
    { value: 'en', label: t('common.languages.english') },
    { value: 'ro', label: t('common.languages.romanian') },
  ], [t]);

  const VAT_OPTIONS = useMemo(() => [
    { value: true, label: t('common.withVat') },
    { value: false, label: t('common.withoutVat') },
  ], [t]);

  const SESSION_LIFETIME_OPTIONS = useMemo(() => [
    // { value: 0, label: t('common.sessionLifetimeOptions.0') },
    { value: 900, label: t('common.sessionLifetimeOptions.15') },
    { value: 1800, label: t('common.sessionLifetimeOptions.30') },
    { value: 3600, label: t('common.sessionLifetimeOptions.60') },
    { value: 86400, label: t('common.sessionLifetimeOptions.1440') },
    // { value: 2592000, label: t('common.sessionLifetimeOptions.43200') },
  ], [t]);

  const EMAIL_ALERT_OPTIONS = useMemo(() => [
    { value: 1, label: t('profile.alertOptions.default') },
    { value: 2, label: t('profile.alertOptions.creator') },
    { value: 3, label: t('profile.alertOptions.both') },
  ], [t]);

  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });

  const validators = {
    defaultCurrency: { required },
    language: { required },
    withVat: {
      validate: value => value !== null || t('errors.fieldBlank'),
    },
    sessionLifetime: { required },
    userPaymentOption: { required },
    userTransportOption: { required },
    technicalSupportEmail: {
      required,
      maxLength: maxLength(50),
      pattern: {
        value: EMAIL_REGEX,
        message: t('errors.invalidEmail'),
      },
    },
    alertEmail: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
      pattern: {
        value: EMAIL_REGEX,
        message: t('errors.invalidEmail'),
      },
    },
    alertOption: { required },
    alertEmailLanguage: { required },
    resellerPaymentOption: { required },
    resellerTransportOption: { required },
  };

  const getAccountSettings = () => {
    setLoading(true);

    fetcher(API_ACCOUNT_SETTINGS).then(res => {
      setPaymentOptions((res?.paymentOptions || []).map(({ id, name }) => ({ value: id, label: name })));
      setTransportOptions((res?.transportOptions || []).map(({ id, name }) => ({ value: id, label: name })));
      reset({
        defaultCurrency: res?.defaultCurrency,
        language: res?.language,
        withVat: res?.withVat,
        sessionLifetime: res?.sessionLifetime,
        userPaymentOption: res?.userPaymentOption?.id,
        userTransportOption: res?.userTransportOption?.id,
        technicalSupportEmail: res?.technicalSupportEmail,
        alertEmail: res?.alertEmail,
        alertOption: res?.alertOption,
        alertEmailLanguage: res?.alertEmailLanguage,
        resellerPaymentOption: res?.resellerPaymentOption?.id,
        resellerTransportOption: res?.resellerTransportOption?.id,
      });
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  // Get Settings on page load
  useEffect(() => {
    if (loggedIn) {
      getAccountSettings();
    }
  }, [location.pathname, query, email, loggedIn]);

  const handleOnChange = (fieldName, value) => {
    setValue(fieldName, value?.selectedItem?.value);
  };

  const handleOnSubmit = async () => {
    analytics.track('updated_settings');
    setErrors([]);
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length) {
      // Stop execution if any errors
      return;
    }

    const {
      defaultCurrency,
      language,
      withVat,
      sessionLifetime,
      userPaymentOption,
      userTransportOption,
      technicalSupportEmail,
      alertEmail,
      alertOption,
      alertEmailLanguage,
      resellerPaymentOption,
      resellerTransportOption,
    } = getValues();

    const parsedData = {
      userId,
      defaultCurrency,
      language,
      ...(!isExternalReseller && {
        withVat: withVat ? 1 : 0,
      }),
      sessionLifetime,
      userPaymentOption,
      userTransportOption,
      technicalSupportEmail,
      ...(isAdmin && {
        alertEmail,
        alertOption,
        alertEmailLanguage,
        resellerPaymentOption,
        resellerTransportOption,
      })
    };

    setSubmitLoading(true);
    fetcher(API_ACCOUNT_SETTINGS, {
      method: 'PUT',
      body: JSON.stringify(parsedData),
    }).then(() => {
      mutate({ language, defaultCurrency, withVat });
      mutateCategories();
      getAccountSettings();
      setErrors([{ type: 'success', message: t('profile.settingsSaved') }]);
    }).catch(() => {
      setErrors([{ type: 'error', message: t('profile.settingsSavedError') }]);
    })
      .finally(() => {
        setSubmitLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  return (
    <AccountSettingsComponent
      loading={ loading }
      isAdmin={ isAdmin }
      isExternalReseller={ isExternalReseller }
      validators={ validators }
      register={ register }
      control={ control }
      watch={ watch }
      rhfErrors={ rhfErrors }
      handleOnChange={ handleOnChange }
      handleOnSubmit={ handleOnSubmit }
      submitLoading={ submitLoading }
      currencyOptions={ CURRENCY_OPTIONS }
      languageOptions={ LANGUAGE_OPTIONS }
      vatOptions={ VAT_OPTIONS }
      sessionLifetimeOptions={ SESSION_LIFETIME_OPTIONS }
      paymentOptions={ paymentOptions }
      transportOptions={ transportOptions }
      emailAlertOptions={ EMAIL_ALERT_OPTIONS }
    />
  );
});

AccountSettings.propTypes = {
  setErrors: func,
};

AccountSettings.displayName = 'AccountSettingsContainer';

export default AccountSettings;
